import {
  fetchSources,
  fetchSourceLastStatus,
  fetchSourceNumberOfContents,
  buildCustomRowsUrl,
  searchContents
} from './actions'
import {
  modalBeeContentDialogState,
  templates,
  modalBeeContentDialogSearchState,
  modalBeeContentDialogResultsState,
  modalBeeContentDialogContentsState
} from './state'

import getIntl from 'Helpers/intl'

import { logAppError } from 'Helpers/logger'

import { IMG_POS_TOP, IMG_SPACE_EQUAL } from '../constants'

export const modalContentDialogCallback = ({ set }) => async (
  resolveCallback,
  rejectCallback
) => {
  set(modalBeeContentDialogState, prev => ({
    ...prev,
    loading: true,
    show: true,
    resolveCallback,
    rejectCallback
  }))

  try {
    const sources = await fetchSources()

    set(modalBeeContentDialogState, prev => ({
      ...prev,
      sources
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'ModalBeeContentDialog',
      Method: 'modalContentDialogCallback'
    })
    set(modalBeeContentDialogState, prev => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    }))
  }

  set(modalBeeContentDialogState, prev => ({
    ...prev,
    loading: false,
    loaded: true
  }))
}

export const onChangeSourceCallback = ({ snapshot, set }) => async sourceId => {
  set(modalBeeContentDialogState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const status = sourceId ? await fetchSourceLastStatus(sourceId) : undefined

    const numberOfContents = sourceId
      ? await fetchSourceNumberOfContents(sourceId)
      : 0

    set(modalBeeContentDialogState, prev => ({
      ...prev,
      sourceId,
      sourceContents: numberOfContents,
      sourceStatus: status
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'ModalBeeContentDialog',
      Method: 'onChangeSourceCallback'
    })
    set(modalBeeContentDialogState, prev => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    }))
  }

  set(modalBeeContentDialogState, prev => ({
    ...prev,
    loading: false
  }))
}

export const onChangeContentsPerRowCallback = ({
  set,
  snapshow
}) => async value => {
  set(
    modalBeeContentDialogState,
    ({
      contentsPerRow: prevContentsPerRow,
      imgPos: prevImgPos,
      imgSpace: prevImgSpace,
      ...prev
    }) => ({
      ...prev,
      contentsPerRow: value,
      imgPos: prevContentsPerRow !== 1 ? IMG_POS_TOP : prevImgPos,
      imgSpace: prevContentsPerRow !== 1 ? IMG_SPACE_EQUAL : prevImgSpace
    })
  )
}

export const onChangeTemplateCallback = ({
  set,
  snapshow
}) => async templateId => {
  const { contentsPerRow, imgPos, imgSpace } = templates[templateId]

  set(modalBeeContentDialogState, prev => ({
    ...prev,
    contentsPerRow,
    imgPos,
    imgSpace
  }))
}

export const modalContentDialogConfirmCallback = ({
  set,
  snapshot,
  reset
}) => async () => {
  const {
    resolveCallback,
    sources,
    sourceId,
    contentsPerRow,
    repeat,
    repeatTimes,
    imgPos,
    imgSpace,
    selectedContents
  } = await snapshot.getPromise(modalBeeContentDialogState)

  const { label: sourceName, guid: sourceGuid } = sources.find(
    ({ value }) => value === sourceId
  )

  try {
    const url = await buildCustomRowsUrl({
      sourceId,
      sourceGuid,
      rows: repeat ? repeatTimes : 1,
      rowContents: contentsPerRow,
      imgPos,
      imgSpace,
      selectedContents
    })

    resolveCallback({ name: sourceName, value: url })
    reset(modalBeeContentDialogState)
    reset(modalBeeContentDialogSearchState)
    reset(modalBeeContentDialogResultsState)
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'ModalBeeContentDialog',
      Method: 'modalContentDialogConfirmCallback'
    })
    set(modalBeeContentDialogContentsState, prev => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    }))
  }
}

export const modalContentDialogCancelCallback = ({
  snapshot,
  reset
}) => async () => {
  const { rejectCallback } = await snapshot.getPromise(
    modalBeeContentDialogState
  )

  rejectCallback()

  reset(modalBeeContentDialogState)
  reset(modalBeeContentDialogSearchState)
  reset(modalBeeContentDialogResultsState)
}

//

export const modalContentDialogStep2Callback = ({
  set,
  snapshot
}) => async () => {
  const {
    sourceId,
    contentsPerRow,
    repeat,
    repeatTimes
  } = await snapshot.getPromise(modalBeeContentDialogState)

  set(modalBeeContentDialogState, prev => ({
    ...prev,
    loading: true
  }))

  const slots = repeat ? repeatTimes * contentsPerRow : contentsPerRow

  try {
    const result = await searchContents(sourceId, {
      pageNumber: 1,
      pageSize: 5
    })
    set(modalBeeContentDialogResultsState, prev => ({ ...prev, ...result }))

    const tmp = new Array(slots).fill(undefined)

    set(modalBeeContentDialogState, prev => ({
      ...prev,
      step: 2,
      selectedContents: tmp.reduce(
        (acc, value, index) => ({ ...acc, [index + 1]: null }),
        {}
      )
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'ModalBeeContentDialog',
      Method: 'modalContentDialogStep2Callback'
    })
    set(modalBeeContentDialogState, prev => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    }))
  }

  set(modalBeeContentDialogState, prev => ({
    ...prev,
    loading: false
  }))
}

export const modalContentDialogSearchCallback = ({ set, snapshot }) => async ({
  pageNumber,
  pageSize,
  orderBy
}) => {
  set(modalBeeContentDialogContentsState, prev => ({ ...prev, fetching: true }))
  const { sourceId } = await snapshot.getPromise(modalBeeContentDialogState)
  const {
    searchBy,
    searchText,
    orderBy: currentOrderBy
  } = await snapshot.getPromise(modalBeeContentDialogSearchState)
  const {
    pageNumber: currentPageNumber,
    pageSize: currentPageSize
  } = await snapshot.getPromise(modalBeeContentDialogResultsState)

  const searchParams = {
    searchBy,
    searchText,
    pageNumber: pageNumber || currentPageNumber,
    pageSize: pageSize || currentPageSize,
    orderBy: orderBy || currentOrderBy
  }

  try {
    const result = await searchContents(sourceId, searchParams)
    set(modalBeeContentDialogSearchState, prev => ({
      ...prev,
      orderBy: searchParams.orderBy
    }))
    set(modalBeeContentDialogResultsState, result)
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'ModalBeeContentDialog',
      Method: 'modalContentDialogSearchCallback'
    })

    set(modalBeeContentDialogContentsState, prev => ({
      ...prev,
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    }))
  }

  set(modalBeeContentDialogContentsState, prev => ({
    ...prev,
    fetching: false
  }))
}
